import { useCallback, useEffect, useMemo, useState } from 'react';
import { SAVE_BUTTON_STATES } from '@urbanx/agx.ui-components2';
import { AgxToastState } from 'types/commonTypes';
import {
  TenantedPropertyDetailsModel,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useInvalidateUseStaffGetTenancyInformationForCampaign,
  useStaffGetTenancyInformationForCampaign,
  useStaffUpdateTenancyInformationForCampaign,
} from '@urbanx/agx.campaigns.client';
import { isDirtyEqual } from 'utils/compares';

interface UseTenancyInformationSectionStateArgs {
  campaignId?: string;
  updateToastState: (toastState: AgxToastState) => void;
}

interface TenancyInformationSectionState {
  tenancyInformation?: TenantedPropertyDetailsModel | undefined;
  setTenancyInformation: (value: TenantedPropertyDetailsModel | undefined) => void;
  hasUnsavedChanges: boolean;
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES];
  isSaving: boolean;
  isPending: boolean;
  isError: boolean;
  handleSaveChanges: () => Promise<void>;
}

const useTenancyInformationSectionState = ({
  campaignId,
  updateToastState,
}: UseTenancyInformationSectionStateArgs): TenancyInformationSectionState => {
  const [tenancyInformation, setTenancyInformation] = useState<
    TenantedPropertyDetailsModel | undefined
  >();
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [hasError, setHasError] = useState(false);

  const {
    data: tenancyInformationResponse,
    isPending,
    isError,
  } = useStaffGetTenancyInformationForCampaign(campaignId ?? '', !!campaignId);

  const invalidateStaffGetTenancyInformation =
    useInvalidateUseStaffGetTenancyInformationForCampaign();
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign();

  const { staffUpdateTenancyInformationForCampaign } = useStaffUpdateTenancyInformationForCampaign({
    onSuccess: () => undefined,
    onError: () => undefined,
  });

  useEffect(() => {
    setTenancyInformation(tenancyInformationResponse?.tenantedPropertyDetails);
  }, [tenancyInformationResponse?.tenantedPropertyDetails]);

  const hasUnsavedChanges = useMemo(() => {
    return !isDirtyEqual(tenancyInformation, tenancyInformationResponse?.tenantedPropertyDetails);
  }, [tenancyInformation, tenancyInformationResponse?.tenantedPropertyDetails]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      setHasSaved(false);
      setHasError(false);
    }
  }, [hasUnsavedChanges]);

  const saveState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING;
    if (hasError) return SAVE_BUTTON_STATES.ERROR;
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED;
    if (hasUnsavedChanges) return SAVE_BUTTON_STATES.UNSAVED;
    return SAVE_BUTTON_STATES.UNSAVED;
  }, [hasError, hasSaved, hasUnsavedChanges, isSaving]);

  const handleSaveChanges = useCallback(async () => {
    if (!campaignId || !hasUnsavedChanges || isSaving) return;

    setIsSaving(true);
    setHasError(false);
    try {
      await staffUpdateTenancyInformationForCampaign({
        campaignId,
        body: {
          tenantedPropertyDetails: tenancyInformation,
        },
      });
      await Promise.all([
        invalidateStaffGetTenancyInformation(campaignId),
        invalidateStaffGetPropertyInformationProgress(campaignId),
      ]);
      setHasSaved(true);
      updateToastState({
        color: 'success',
        message: 'Tenancy information saved successfully',
        open: true,
      });
    } catch {
      setHasError(true);
      updateToastState({
        color: 'error',
        message: 'Error saving tenancy information',
        open: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    campaignId,
    hasUnsavedChanges,
    isSaving,
    invalidateStaffGetPropertyInformationProgress,
    invalidateStaffGetTenancyInformation,
    staffUpdateTenancyInformationForCampaign,
    tenancyInformation,
    updateToastState,
  ]);

  return {
    tenancyInformation,
    setTenancyInformation,
    hasUnsavedChanges,
    saveState,
    isSaving,
    isPending,
    isError,
    handleSaveChanges,
  };
};

export default useTenancyInformationSectionState;
